import { Checkbox, DismissableChip, Icon, Separator } from "@/shared/components/ui";
import { useRadixBaseUiInterop } from "@/shared/hooks/use-radix-base-ui-interop.ts";
import { cn } from "@/shared/utils";
import { Combobox } from "@base-ui/react/combobox";
import { ButtonHTMLAttributes, Dispatch, RefObject, SetStateAction, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useOverflowList } from "./overflow-list/use-overflow-list";

export type RankFunction = (value: string, search: string, keywords?: string[]) => number;

/**
 * Option interface for MultiSelect component
 */
interface MultiSelectOption {
    /** The text to display for the option. */
    label: string;
    /** The text to display for the option when it's selected, defaults to `label`. */
    badgeLabel?: string;
    /** The unique value associated with the option. */
    value: string;
    /** Whether this option is disabled */
    disabled?: boolean;
}

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value"> {
    /**
     * An array of option objects or groups to be displayed in the multi-select component.
     */
    options: MultiSelectOption[];
    /**
     * Callback function triggered when the selected values change.
     * Receives an array of the new selected values.
     */
    onValueChange: (value: string[]) => void;

    /** The current value. */
    value: string[];

    /** The default selected values when the component mounts. */
    defaultValue?: string[];

    /** Function to sort and rank options */
    rankFn?: RankFunction;

    /**
     * Placeholder text to be displayed when no values are selected.
     * Optional, defaults to "Selecteer ...".
     */
    placeholder?: string;

    /**
     * Additional class names to apply custom styles to the multi-select component.
     * Optional, can be used to add custom styles.
     */
    className?: string;

    /**
     * Custom CSS class for the popover content.
     * Optional, can be used to customize popover appearance.
     */
    popoverClassName?: string;

    /**
     * If true, disables the component completely.
     * Optional, defaults to false.
     */
    disabled?: boolean;

    /**
     * Configuration for search input
     * Can be boolean true for default search or an object for custom placholder and empty results message.
     */
    search?: boolean | { placeholder?: string; emptyMessage?: string };

    /**
     * If true, the component will reset its internal state when defaultValue changes.
     * Useful for React Hook Form integration and form reset functionality.
     * Optional, defaults to true.
     */
    resetOnDefaultValueChange?: boolean;

    /**
     * If true, automatically closes the popover after selecting an option.
     * Useful for single-selection-like behavior or mobile UX.
     * Optional, defaults to false.
     */
    closeOnSelect?: boolean;
    "data-testid"?: string;
    portalContainer?: RefObject<HTMLElement | null>;
}

const isArrayOfString = (value: unknown): value is string[] => Array.isArray(value) && value.every((val) => typeof val === "string");

export function MultiSelect({
    options,
    value,
    onValueChange,
    defaultValue = [],
    placeholder = "Selecteer ...",
    portalContainer,
    className,
    popoverClassName,
    disabled = false,
    resetOnDefaultValueChange = true,
    closeOnSelect = false,
    search = true,
    rankFn,
    "data-testid": dataTestid = "multi-select",
    ...props
}: Readonly<MultiSelectProps>) {
    const radixRef = useRadixBaseUiInterop();
    const [selectedValues, setSelectedValues] = useState<string[]>(() => {
        if (isArrayOfString(value)) {
            return value.map((v) => v.toLowerCase());
        }
        return defaultValue.map((v) => v.toLowerCase());
    });
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const multiSelectId = useId();
    const triggerDescriptionId = `${multiSelectId}-description`;
    const selectedCountId = `${multiSelectId}-count`;

    const prevDefaultValueRef = useRef<string[]>(defaultValue);

    const arraysEqual = useCallback((a: string[], b: string[]): boolean => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, index) => val.toLowerCase() === sortedB[index].toLowerCase());
    }, []);

    const optionValueMap = useMemo(() => {
        return new Map(options.map((option) => [option.value.toLowerCase(), option]));
    }, [options]);

    const getOptionByValue = useCallback(
        (value: string): MultiSelectOption | undefined => {
            return optionValueMap.get(value.toLowerCase());
        },
        [optionValueMap]
    );

    const handleValueChange = useCallback(
        (newValues: MultiSelectOption[]) => {
            setSelectedValues(newValues.map((o) => o.value));
            onValueChange(newValues.map((o) => o.value));
            if (closeOnSelect) setIsPopoverOpen(false);
        },
        [onValueChange, closeOnSelect]
    );

    const selectedOptions = selectedValues.map((o) => getOptionByValue(o)).filter((o): o is MultiSelectOption => Boolean(o));

    const filter = Combobox.useFilter({ sensitivity: "base" });
    const createDefaultFilterFn = useCallback(
        (query: string) => (option: MultiSelectOption) => {
            return filter.contains(option.label, query) || filter.contains(option.value, query);
        },
        [filter]
    );

    const filteredOptions = useMemo(() => {
        if (!rankFn) {
            return options.filter(createDefaultFilterFn(searchValue));
        }

        const scored = options
            .map((opt) => ({
                opt,
                score: rankFn(opt.label, searchValue, [opt.value]),
            }))
            .filter(({ score }) => score > 0)
            .sort((a, b) => b.score - a.score);

        return scored.map(({ opt }) => opt);
    }, [rankFn, options, createDefaultFilterFn, searchValue]);

    useEffect(() => {
        if (!resetOnDefaultValueChange) return;
        const prevDefaultValue = prevDefaultValueRef.current;
        if (!arraysEqual(prevDefaultValue, defaultValue)) {
            if (!arraysEqual(selectedValues, defaultValue)) {
                setSelectedValues(defaultValue);
            }
            prevDefaultValueRef.current = [...defaultValue];
        }
    }, [defaultValue, selectedValues, arraysEqual, resetOnDefaultValueChange]);

    useEffect(() => {
        if (!isPopoverOpen) {
            setSearchValue("");
        }
    }, [isPopoverOpen]);

    return (
        <Combobox.Root
            multiple
            value={selectedOptions}
            onValueChange={handleValueChange}
            open={isPopoverOpen}
            onOpenChange={(open) => setIsPopoverOpen(open)}
            inputValue={searchValue}
            onInputValueChange={(val, eventDetails) => {
                if (eventDetails.reason !== "input-clear") {
                    setSearchValue(val);
                }
            }}
            modal={false}
            disabled={disabled}
            filteredItems={filteredOptions}
        >
            <div id={triggerDescriptionId} className="sr-only">
                Multi-select dropdown. Use arrow keys to navigate, Enter to select, and Escape to close.
            </div>

            <Combobox.Trigger
                data-testid={dataTestid}
                disabled={disabled}
                aria-describedby={`${triggerDescriptionId} ${selectedCountId}`}
                aria-label={`Multi-select: ${selectedValues.length} of ${options.length} options selected. ${placeholder}`}
                className={cn(
                    "border-input-default bg-input placeholder:text-content-placeholder hover:border-interactive-hover focus-within:outline-focus rounded-m text-s relative flex h-10 w-full max-w-full min-w-0 grow items-center border p-1 focus-within:border-transparent focus-within:outline-2 disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                {...props}
            >
                <MultiSelectInput
                    searchValue={searchValue}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    onValueChange={onValueChange}
                    placeholder={placeholder}
                />
            </Combobox.Trigger>

            <Combobox.Portal container={portalContainer ?? radixRef ?? undefined}>
                <Combobox.Backdrop />
                <Combobox.Positioner
                    align="start"
                    sideOffset={4}
                    className="bg-popover text-popover-foreground border-input-default -scroll-py-1 z-50 max-h-[60vh] w-auto min-w-75 rounded-md border shadow-md outline-hidden"
                >
                    <Combobox.Popup
                        data-combobox-popup
                        aria-label="Available options"
                        className={cn("touch-manipulation p-0", popoverClassName)}
                    >
                        {search && (
                            <div className="flex h-9 items-center gap-2 border-b px-3">
                                <Icon name="search" size="xs" className="shrink-0 opacity-50" />
                                <Combobox.Input
                                    className="placeholder:text-muted-foreground text-s flex h-10 w-full rounded-md bg-transparent py-3 outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                                    placeholder={typeof search === "object" ? search.placeholder : undefined}
                                    aria-label="Search through available options"
                                    aria-describedby={`${multiSelectId}-search-help`}
                                    data-testid={`${dataTestid}-input`}
                                />
                                <div id={`${multiSelectId}-search-help`} className="sr-only">
                                    Type to filter options. Use arrow keys to navigate results.
                                </div>
                            </div>
                        )}
                        <Combobox.Empty>
                            <div className="text-s py-6 text-center">
                                {typeof search === "object" ? search.emptyMessage : "Geen resultaten."}
                            </div>
                        </Combobox.Empty>
                        <Combobox.List
                            className="multiselect-scrollbar overscroll-behavior-y-contain max-h-[40vh] overflow-y-auto"
                            data-testid={`${dataTestid}-list`}
                        >
                            {(option: MultiSelectOption) => {
                                const isSelected = selectedValues.includes(option.value.toLowerCase());
                                return (
                                    <Combobox.Item
                                        key={option.value.toLowerCase()}
                                        value={option}
                                        disabled={option.disabled}
                                        aria-label={`${option.label}${isSelected ? ", selected" : ", not selected"}${option.disabled ? ", disabled" : ""}`}
                                        className={cn(
                                            "data-highlighted:bg-selected-default [&_svg:not([class*='text-'])]:text-muted-foreground text-content-default text-m relative flex cursor-default items-center gap-2 px-4 py-1.5 outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                                            option.disabled && "cursor-not-allowed opacity-50"
                                        )}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => setSelectedValues((prev) => prev.filter((item) => item !== option.value))}
                                        />
                                        <span>{option.label}</span>
                                    </Combobox.Item>
                                );
                            }}
                        </Combobox.List>
                    </Combobox.Popup>
                </Combobox.Positioner>
            </Combobox.Portal>
        </Combobox.Root>
    );
}

type MultiSelectInputProps = Pick<MultiSelectProps, "onValueChange"> & {
    searchValue: string;
    placeholder?: string;
    selectedValues: string[];
    setSelectedValues: Dispatch<SetStateAction<string[]>>;
};

function MultiSelectInput({ placeholder, selectedValues, setSelectedValues, onValueChange, searchValue }: MultiSelectInputProps) {
    return (
        <Combobox.InputGroup className="group flex h-10 w-full items-center">
            <Combobox.Chips className="text-m text-content-placeholder flex h-10 flex-1 items-center">
                <ChipList
                    searchValue={searchValue}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    onValueChange={onValueChange}
                    placeholder={placeholder}
                />
            </Combobox.Chips>
            <div className="flex items-center justify-between">
                <Combobox.Clear
                    nativeButton={false}
                    render={(props) => <div {...props} />}
                    aria-label={`Clear all ${selectedValues.length} selected options`}
                    className="text-muted-foreground hover:text-foreground focus:ring-ring mx-2 flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm focus:ring-2 focus:ring-offset-1 focus:outline-none"
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={(e) => e.preventDefault()}
                >
                    <Icon name="close" className="text-content-subtle hover:bg-hover cursor-pointer" />
                </Combobox.Clear>
                <Separator orientation="vertical" className="border-emphasis flex h-full min-h-6" />
                <Icon
                    name="chevron-down"
                    size="m"
                    className="text-content-emphasis hover:bg-hover cursor-pointer transition-transform duration-300 group-data-popup-open:scale-y-[-1]"
                    aria-hidden="true"
                />
            </div>
        </Combobox.InputGroup>
    );
}

type ChipListProps = MultiSelectInputProps;
function ChipList({ selectedValues, setSelectedValues, onValueChange, placeholder, searchValue }: ChipListProps) {
    const { containerRef, visibleCount, hiddenCount, overflowIndicatorRef, showOverflow, phase } = useOverflowList<
        HTMLDivElement,
        HTMLSpanElement
    >({ itemCount: selectedValues.length });

    const clearExtraOptions = () => {
        const newValues = selectedValues.slice(0, visibleCount);
        setSelectedValues(newValues);
        onValueChange(newValues);
    };

    useEffect(() => {
        if (containerRef.current && searchValue.trim().length > 0) {
            containerRef.current.scrollTo(0, 0);
        }
    }, [containerRef, searchValue]);

    if (selectedValues.length === 0) {
        return (
            <div className="text-m flex w-full items-center justify-between px-3">
                <span className="text-content-placeholder min-w-0 overflow-hidden font-normal">{placeholder}</span>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="flex flex-wrap items-center gap-1">
            <Combobox.Value>
                {(selectedValue: MultiSelectOption[]) => (
                    <>
                        {selectedValue.map((item, index) => {
                            const visible = phase === "measuring" || index < visibleCount;
                            if (!visible) return null;
                            return (
                                <Combobox.Chip
                                    key={item.value}
                                    render={(props, state) => (
                                        <DismissableChip
                                            checked
                                            onChange={() => {}}
                                            label={item.badgeLabel ?? item.label}
                                            className="group data-overflow-hidden:v flex shrink-0 items-center gap-1 whitespace-nowrap [&>svg]:pointer-events-auto"
                                            toggle={() => {
                                                const newValues = selectedValues.filter((v) => v !== item.value);
                                                setSelectedValues(newValues);
                                                onValueChange(newValues);
                                            }}
                                            {...props}
                                            {...state}
                                            onPointerDown={(e) => e.preventDefault()}
                                            onClick={(e) => e.preventDefault()}
                                        />
                                    )}
                                />
                            );
                        })}
                        {showOverflow && (
                            <DismissableChip
                                ref={overflowIndicatorRef}
                                className="shrink-0 cursor-pointer whitespace-nowrap [&>svg]:pointer-events-auto"
                                checked
                                onChange={() => clearExtraOptions()}
                                toggle={() => clearExtraOptions()}
                                label={`+${hiddenCount}`}
                                onPointerDown={(e) => e.preventDefault()}
                                onClick={(e) => e.preventDefault()}
                            />
                        )}
                    </>
                )}
            </Combobox.Value>
        </div>
    );
}

MultiSelect.displayName = "MultiSelect";
export type { MultiSelectOption, MultiSelectProps };
